<template>
  <v-card class="oderon-card">
    <OSimpleDialog
      ref="requestResourcesDialog"
      title="Solicitar Lista de Arquivos"
      :maxWidth="400"
      txtApply="Solicitar"
      @apply="doRequestResourceList"
    >
      <div class="pa-6">
        <v-date-picker
          no-title
          scrollable
          full-width
          show-adjacent-months
          v-model="requestResourceListFromDate"
        />
        <p class="caption mt-6">
          Selecione a data de início da consulta. Evite datas mais de 1 mês
          atrás. A lista de arquivos será transmitida pela câmera e
          sobrescreverá a lista atual.
        </p>
        <p class="caption red" v-if="E_NO_DATE">
          Selecione uma data!
        </p>
      </div>
    </OSimpleDialog>

    <div class="compact-bar">
      <v-btn icon @click="$emit('reload')">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <div class="title">Vídeos na memória da câmera</div>
      <v-spacer />
      <v-btn
        v-if="cameraDef.model == 'jc371'"
        small
        @click="openRequestResourcesDialog"
      >
        Solicitar...
      </v-btn>
      <v-spacer />
      <div class="subtitle mr-4">Horários em {{ tzSubtitle }}</div>
      <v-btn icon @click="expanded = !expanded">
        <v-icon>{{ expanded ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
      </v-btn>
    </div>

    <OLoadingLine absolute :value="loader.isLoading" />

    <div v-if="expanded" class="pa-2">
      <HorizontalHeatmap
        v-if="series != null"
        :dateRange="dateRange"
        :series="series"
        @segmentClick="handleClick"
      />
    </div>

    <v-card-text v-if="loader.error">
      <v-alert icon="$error" color="red">{{ loader.error }}</v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment'
import { tzSubtitle } from './tz-offset.js'
import Loader from 'oderon-vue/models/Loader.mjs'
import utils from 'oderon-commons/src/utils.js'
import { go } from '@/models'
import HorizontalHeatmap from 'oderon-vue/cpts/charts/HorizontalHeatmap.vue'
import OSimpleDialog from 'oderon-vue/cpts/dialogs/OSimpleDialog.vue'

export default {
  components: { HorizontalHeatmap, OSimpleDialog },
  expose: ['reload'],
  props: {
    cameraDef: {},
    date: {}
  },
  data() {
    return {
      tzSubtitle,
      dateRange: null,
      series: null,
      loader: new Loader(),
      expanded: false,

      E_NO_DATE: false,
      requestResourceListFromDate: null
    }
  },
  methods: {
    async reload() {
      await this.loader.follow(async () => {
        let t0 = utils.sod(this.date, 0, 'day')
        let t1 = utils.sod(this.date, 1, 'day')
        this.dateRange = [t0, t1]

        await this.fetchData()
      })
    },

    async fetchData() {
      this.series = await fetchItems({
        cameraDef: this.cameraDef,
        dateRange: this.dateRange
      })
    },

    async handleClick(segment) {
      return await this.loader.follow(async () => {
        let cameraDef = this.cameraDef

        if (canCancel({ cameraDef, segment })) {
          await cancelRequest({ cameraDef, segment })
          await this.fetchData()
        } else if (canRequest({ cameraDef, segment })) {
          await createRequest({ cameraDef, segment })
          await this.fetchData()
        }

        // aqui, não pode fazer nada (e.g. arquivo já baixado)
      })
    },

    openRequestResourcesDialog() {
      this.requestResourceListFromDate = null
      this.E_NO_DATE = false
      this.$refs.requestResourcesDialog.open()
    },

    async doRequestResourceList() {
      if (this.requestResourceListFromDate == null) {
        this.E_NO_DATE = true
        return
      }

      this.$refs.requestResourcesDialog.dismiss()
      await this.$rootLoading(async () => {
        await go.post('/cameras/jimi2/camera-files/request-resource-list', {
          imei: this.cameraDef.imei,
          fromDate: this.requestResourceListFromDate
        })

        this.$snack('Comando enviado; aguardando transmissão da câmera...', {
          timeout: 5000,
          color: 'yellow'
        })
      })
    }
  }
}

async function fetchItems({ cameraDef, dateRange }) {
  if (cameraDef == null || cameraDef.imei == null)
    throw new Error('Falta o IMEI para buscar itens')

  if (dateRange == null) throw new Error('Falta a data para buscar itens')

  if (cameraDef.model == 'jc371') {
    return await fetchJC371Heatmap({ cameraDef, dateRange })
  } else {
    return await fetchJC400Heatmap({ cameraDef, dateRange })
  }
}

async function fetchJC371Heatmap({ cameraDef, dateRange }) {
  let ps = {
    imei: cameraDef.imei,
    imeis: cameraDef.imei,
    fromDate: dateRange[0],
    untilDate: dateRange[1],
    limit: 10000,
    order: 'timestamp asc, id asc'
  }

  let { rows: available } = await go.get(
    '/cameras/jimi2/camera-files/heatmap',
    ps
  )
  let { rows: uploadRequests } = await go.get(
    `/cameras/jimi2/upload-requests`,
    ps
  )

  // Para cada item da lista Available, verifica se existe um
  // Request correspondente e usa essa info para determinar o
  // status/cor. Nesta implementação, como todo request tem o
  // ID do disponível que o originou, vamos usar esse id para
  // corresponder.
  let requestsByAvailableId = utils.keyBy(uploadRequests, 'resourceId')

  for (let row of available) {
    row.timestamp = toDate(row.timestamp)

    row.uploadRequest = requestsByAvailableId[row.id]
    if (row.uploadRequest == null) {
      row.cssClass = 'available'
    } else if (row.uploadRequest.cancelledAt != null) {
      row.cssClass = 'cancelled'
    } else if (row.uploadRequest.completedAt != null) {
      row.cssClass = 'completed'
    } else {
      row.cssClass = 'pending'
    }
  }

  // Agrupa os disponíveis por canal para montar um heatmap em cada um.
  let channels = splitChannels(available)
  return channels
}

async function fetchJC400Heatmap({ cameraDef, dateRange }) {
  let { rows } = await go.get('/cameras/jimi1/filelist', {
    imei: cameraDef.imei,
    fromDate: dateRange[0],
    untilDate: dateRange[1],
    limit: 10000,
    order: 'timestamp asc, id asc'
  })

  for (let row of rows) {
    row.timestamp = toDate(row.timestamp)

    // No oderon-jimi1, não temos durationS de cada FileItem;
    // assumiremos 1min que é a duração padrão
    row.durationS = 60

    // Ao contrário do oderon-jimi2, aqui os FileItems e seus
    // "uploadRequests" estão no mesmo objeto, e não existe
    // status "cancelled"
    if (row.status == null) {
      row.cssClass = 'available'
    } else if (row.status == 'pend') {
      row.cssClass = 'pending'
    } else if (row.status == 'down') {
      row.cssClass = 'completed'
    }
  }

  // Agrupa os disponíveis por canal para montar um heatmap em cada um.
  let channels = splitChannels(rows)
  return channels
}

// converte de string para Date
function toDate(x) {
  return moment(x)
    .startOf('second')
    .toDate()
}

function splitChannels(rows) {
  let channels = [1, 2, 3].map((number) => {
    return {
      number,
      label: 'Canal ' + number,

      // cada datapoint tem { id, timestamp, durationS, cssClass }
      data: rows.filter((x) => x.channel == number)
    }
  })
  return channels
}

async function createRequest({ cameraDef, segment }) {
  if (cameraDef == null || cameraDef.imei == null)
    throw new Error('Falta o IMEI para solicitar upload')

  if (cameraDef.model == 'jc371') {
    let { timestamp, channel } = segment
    if (timestamp == null) throw new Error('Falta timestamp')
    if (channel == null) throw new Error('Falta channel')

    await go.post('/cameras/jimi2/upload-requests', {
      imei: cameraDef.imei,
      timestamp,
      channel,
      resourceId: segment.id
    })
  } else {
    let fileItemId = segment.id
    await go.post(`/cameras/jimi1/filelist/${fileItemId}/request-file`)
  }
}

function canCancel({ cameraDef, segment }) {
  if (cameraDef.model == 'jc371') {
    let ur = segment.uploadRequest
    return ur != null && ur.completedAt == null
  } else {
    return segment.status == 'pend'
  }
}

function canRequest({ cameraDef, segment }) {
  if (cameraDef.model == 'jc371') {
    let ur = segment.uploadRequest
    return ur == null || ur.cancelledAt != null
  } else {
    return segment.status == null
  }
}

async function cancelRequest({ cameraDef, segment }) {
  if (cameraDef == null || cameraDef.imei == null)
    throw new Error('Falta o IMEI para cancelar upload')

  if (cameraDef.model == 'jc371') {
    let id = segment.uploadRequest.id
    await go.delete('/cameras/jimi2/upload-requests/' + id)
  } else {
    let id = segment.id
    await go.delete('/cameras/jimi1/filelist/' + id)
  }
}
</script>

<style lang="sass">
@use "sass:color"

@mixin lightHover($fill, $pct)
  fill: $fill
  &:hover
    fill: color.adjust($fill, $lightness: $pct)

g.series rect
  cursor: pointer

  &.available
    @include lightHover(#ccc, 10%)

  &.completed
    @include lightHover(OliveDrab, 10%)

  &.pending
    @include lightHover(Gold, 10%)

  &.cancelled
    @include lightHover(Crimson, 10%)
</style>
